// Capa de acceso a datos del MVP.
//
// IMPORTANTE: esta es la ÚNICA pieza que sabe que los datos son hardcodeados.
// Mantiene exactamente la misma firma que tendría con un backend real, así que
// los componentes (Dashboard, EmployeesList, etc.) no cambian.
//
// Para conectar un backend de verdad en el futuro: reemplazar el cuerpo de cada
// función por un fetch() al servidor y borrar lib/store.ts + data/.
import { generateReport } from './reportEngine';
import {
  initStore,
  getEmployees,
  updateEmployee as storeUpdateEmployee,
  getReportData,
  resetDemo as storeResetDemo,
} from './store';
import type { SeedEmployee } from '../data/types';

// Pequeño delay para simular latencia de red (hace el demo más realista y
// evita parpadeos de "loading" instantáneos). Quitar si molesta.
const simulateLatency = () => new Promise((r) => setTimeout(r, 120));

// ---- Auth (stub local: cualquier contraseña entra en el demo) ----

export async function login(_password: string): Promise<{ token: string }> {
  await simulateLatency();
  const token = `demo-${Date.now()}`;
  localStorage.setItem('fycom_token', token);
  return { token };
}

export async function verifySession(): Promise<boolean> {
  return Boolean(localStorage.getItem('fycom_token'));
}

// ---- Reportes ----

export async function fetchPeriodReport(startDate: string, endDate: string) {
  await initStore(); // asegura que los time entries estén cargados
  const areas = generateReport(getReportData(), { startDate, endDate });
  return { startDate, endDate, areas };
}

// Reporte de UN colaborador para un período dado (reusa generateReport y
// extrae su fila de la card en la que haya caído).
export async function fetchEmployeeReport(
  employeeId: string,
  startDate: string,
  endDate: string
) {
  await initStore();
  const areas = generateReport(getReportData(), { startDate, endDate });
  for (const area of areas) {
    const found = area.employees.find((e) => e.employeeId === employeeId);
    if (found) return found;
  }
  return null;
}

// ---- Colaboradores ----

export async function fetchEmployees(): Promise<SeedEmployee[]> {
  await simulateLatency();
  return getEmployees();
}

export async function updateEmployee(
  id: string,
  updates: Partial<Omit<SeedEmployee, 'id'>>
): Promise<SeedEmployee> {
  await simulateLatency();
  const updated = storeUpdateEmployee(id, updates);
  if (!updated) {
    throw new Error('Colaborador no encontrado');
  }
  return updated;
}

// ---- Reset del demo (borra ediciones, vuelve al seed) ----

export function resetDemo(): void {
  storeResetDemo();
}

// ---- Endpoints sin backend en el MVP ----
// Carga de CSV y mapeo de nombres requieren un backend que procese archivos.
// En el demo no aplican; se dejan como stubs no-op para no romper imports.

export async function uploadCsv(_type: string, _file: File) {
  await simulateLatency();
  return {
    inserted: 0,
    updated: 0,
    unmatchedNames: [] as string[],
    note: 'La carga de CSV no está disponible en el demo.',
  };
}

export async function fetchPendingMappings() {
  return { pending: [] as any[] };
}

export async function confirmMapping(
  _rawName: string,
  _employeeId: string,
  _source: string
) {
  return { ok: true };
}
